// eventRecovery.js — P2, reentrega de OPERATIONAL_EVENT perdido.
//
// O servidor grava o evento no journal (tabela events) antes de avisar pelo
// socket; o aviso em si pode se perder (socket caido, servidor reiniciado
// entre gravar e avisar). Ao (re)conectar, esta rotina rele do journal o que
// foi gravado depois do ultimo cursor (GET /api/events/operational/recovery)
// e entrega aos mesmos ouvintes do aviso realtime.
//
// - Entrega pelo menos uma vez: o servidor rele com margem; aqui o eventId
//   ja entregue (realtime ou releitura) e descartado, entao o ouvinte nao ve
//   o mesmo evento duas vezes na mesma pagina.
// - Uma operacao por vez: trocou a operacao, cursor e vistos recomecam;
//   resposta de uma operacao antiga e descartada.
// - Primeira conexao so marca o cursor (a tela ja carregou o estado).
// - Falha de leitura nao avanca o cursor: a proxima conexao tenta de novo.
// Sem dependencia de React ou do navegador (testado em
// test/cis/eventRecovery.test.js).

const MAX_SEEN = 5000;
const MAX_PAGES = 10;

const eventKey = (event = {}) => event.id || event.eventId || event.event_id || null;

export function createEventRecovery({ fetchPage, deliver, maxSeen = MAX_SEEN, maxPages = MAX_PAGES }) {
  let operationId = null;
  let cursor = null;
  let seen = new Set();
  let running = null;
  let generation = 0;

  function remember(key) {
    seen.add(key);
    if (seen.size > maxSeen) seen.delete(seen.values().next().value);
  }

  function setOperation(nextOperationId) {
    const next = nextOperationId || null;
    if (next === operationId) return;
    operationId = next;
    cursor = null;
    seen = new Set();
    running = null;
    generation += 1;
  }

  // Aviso realtime: true se for novo (entregar), false se ja entregue.
  function accept(event) {
    const key = eventKey(event);
    if (!key) return true;
    if (seen.has(key)) return false;
    remember(key);
    return true;
  }

  async function run(forOperationId, forGeneration) {
    let pages = 0;
    for (;;) {
      const page = await fetchPage({ operationId: forOperationId, after: cursor });
      if (forGeneration !== generation) return { status: 'stale' };
      const startingPoint = cursor === null;
      for (const event of startingPoint ? [] : (page?.events || [])) {
        if (accept(event)) deliver({ ...event, operationId: forOperationId, recovered: true });
      }
      if (Number.isInteger(page?.cursor)) cursor = page.cursor;
      pages += 1;
      if (startingPoint || !page?.hasMore || pages >= maxPages) return { status: 'ok', cursor };
    }
  }

  // Chamada em cada connect/reconnect. Uma leitura por vez.
  function sync() {
    if (!operationId) return Promise.resolve({ status: 'skipped' });
    if (running) return running;
    const forGeneration = generation;
    const current = run(operationId, forGeneration)
      .catch((error) => ({ status: 'error', message: error?.message || String(error) }))
      .finally(() => { if (generation === forGeneration) running = null; });
    running = current;
    return current;
  }

  return {
    setOperation,
    accept,
    sync,
    getCursor: () => cursor,
  };
}
